import React, { useEffect, useState } from "react";
import { Users, ShieldCheck, TrendingUp, Award } from "lucide-react";
import api from "../api";

function Card({ children, className = "" }) {
  return <div className={`rounded-2xl border border-slate-200 bg-white p-5 shadow-sm md:p-6 ${className}`}>{children}</div>;
}

const BAR_COLORS = ["bg-violet-500", "bg-indigo-500", "bg-sky-500", "bg-emerald-500", "bg-amber-500"];

function Ring({ value, label }) {
  const deg = (value / 100) * 360;
  return (
    <div className="flex flex-col items-center">
      <div className="flex h-28 w-28 items-center justify-center rounded-full" style={{ background: `conic-gradient(#7c3aed ${deg}deg, #ede9fe ${deg}deg)` }}>
        <div className="flex h-20 w-20 flex-col items-center justify-center rounded-full bg-white">
          <span className="text-xl font-bold text-slate-900">{value}%</span>
        </div>
      </div>
      <span className="mt-3 text-sm font-medium text-slate-600">{label}</span>
    </div>
  );
}

export default function Metrics() {
  const [data, setData] = useState(null);
  const [error, setError] = useState("");

  useEffect(() => {
    api.get("/metrics").then((r) => setData(r.data)).catch((e) => setError(e.response?.data?.detail || "Unable to load metrics"));
  }, []);

  if (error) return <div className="text-sm text-rose-600">{error}</div>;
  if (!data) return <div className="text-sm text-slate-400">Loading metrics…</div>;

  const stats = [
    { label: "Total users", value: data.total_users, Icon: Users },
    { label: "Applicants", value: data.total_applicants, Icon: TrendingUp },
    { label: "Compliance", value: `${data.compliance}%`, Icon: ShieldCheck },
    { label: "Certified", value: data.certified, Icon: Award },
  ];

  const maxStage = Math.max(1, ...data.stage_distribution.map((s) => s.value));

  return (
    <div className="space-y-6">
      <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-4">
        {stats.map((s) => (
          <Card key={s.label}>
            <div className="flex items-start justify-between">
              <span className="text-xs font-semibold uppercase tracking-wide text-slate-400">{s.label}</span>
              <s.Icon className="h-4 w-4 text-violet-500" />
            </div>
            <div className="mt-3 text-2xl font-bold text-slate-900">{s.value}</div>
          </Card>
        ))}
      </div>

      <div className="grid grid-cols-1 gap-6 lg:grid-cols-2">
        <Card>
          <h2 className="text-base font-bold text-slate-900">Onboarding &amp; compliance</h2>
          <p className="text-sm text-slate-500">Average across all applicants.</p>
          <div className="mt-6 flex items-center justify-around">
            <Ring value={data.avg_onboarding} label="Avg. onboarding" />
            <Ring value={data.compliance} label="Docs approved" />
          </div>
        </Card>

        <Card>
          <h2 className="text-base font-bold text-slate-900">Applicants by stage</h2>
          <p className="text-sm text-slate-500">Where your pipeline currently sits.</p>
          <div className="mt-5 space-y-3">
            {data.stage_distribution.map((s, i) => (
              <div key={s.label}>
                <div className="mb-1 flex items-center justify-between text-sm">
                  <span className="text-slate-600">{s.label}</span>
                  <span className="font-semibold text-slate-800">{s.value}</span>
                </div>
                <div className="h-2.5 w-full overflow-hidden rounded-full bg-slate-100">
                  <div className={`h-full rounded-full ${BAR_COLORS[i % BAR_COLORS.length]}`} style={{ width: `${(s.value / maxStage) * 100}%` }} />
                </div>
              </div>
            ))}
          </div>
        </Card>
      </div>

      <Card>
        <h2 className="text-base font-bold text-slate-900">Document compliance</h2>
        <div className="mt-4 grid grid-cols-1 gap-4 sm:grid-cols-3">
          <div className="rounded-xl border border-slate-100 p-4">
            <div className="text-xs uppercase tracking-wide text-slate-400">Approved docs</div>
            <div className="mt-1 text-xl font-bold text-slate-900">{data.docs_approved}</div>
          </div>
          <div className="rounded-xl border border-slate-100 p-4">
            <div className="text-xs uppercase tracking-wide text-slate-400">Total required</div>
            <div className="mt-1 text-xl font-bold text-slate-900">{data.docs_total}</div>
          </div>
          <div className="rounded-xl border border-slate-100 p-4">
            <div className="text-xs uppercase tracking-wide text-slate-400">Certified agents</div>
            <div className="mt-1 text-xl font-bold text-slate-900">{data.certified}</div>
          </div>
        </div>
      </Card>
    </div>
  );
}
